import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.bootstrap5.css';
import fa from "datatables.net-plugins/i18n/fa.mjs";

const MOBILE_MAX_WIDTH = 768;
const MOBILE_SCROLL_OFFSET = 36;
const MOBILE_SCROLL_DELAY = 320;
const IGNORED_DATASET_KEYS = new Set([
    'placeholder',
    'tomSelect',
    'noTomSelect',
    'wpTrigger',
    'wpTarget',
]);

function parseDatasetValue(value) {
    if (value === 'null') {
        return null;
    }

    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    if (/^-?\d+(\.\d+)?$/.test(value)) {
        return Number(value);
    }

    if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
        try {
            return JSON.parse(value);
        } catch (error) {
            return value;
        }
    }

    return value;
}

function getSettingsFromDataset(selectEl) {
    const dataset = selectEl.dataset || {};

    return Object.entries(dataset).reduce((settings, [key, value]) => {
        if (IGNORED_DATASET_KEYS.has(key) || value === '') {
            return settings;
        }

        settings[key] = parseDatasetValue(value);
        return settings;
    }, {});
}

function resolveAriaLabel(selectEl) {
    const ariaLabel = selectEl.getAttribute('aria-label');
    if (ariaLabel) {
        return ariaLabel;
    }

    if (!selectEl.id) {
        return null;
    }

    const label = document.querySelector(`label[for="${selectEl.id}"]`);
    return label ? label.textContent.trim() : null;
}

function applyVisualStylesFromSelect(selectEl, instance) {
    const computed = window.getComputedStyle(selectEl);

    if (instance.wrapper) {
        instance.wrapper.style.setProperty('color', computed.color);
    }

    if (instance.control) {
        instance.control.style.setProperty('color', computed.color);
    }
}

function isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
}

function shouldApplyMobileScrollFix() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches || isTouchDevice();
}

function clearScheduledMobileScroll(instance) {
    if (!instance._mobileScrollTimeouts) {
        return;
    }

    instance._mobileScrollTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    instance._mobileScrollTimeouts = [];
}

function getStickyHeaderOffset() {
    const header = document.querySelector('.header');
    if (!header) {
        return MOBILE_SCROLL_OFFSET;
    }

    const headerRect = header.getBoundingClientRect();
    const headerStyles = window.getComputedStyle(header);
    const headerTop = Number.parseFloat(headerStyles.top) || 0;

    return Math.max(MOBILE_SCROLL_OFFSET, headerRect.height + headerTop + MOBILE_SCROLL_OFFSET);
}

function scrollTomSelectIntoView(instance) {
    if (!shouldApplyMobileScrollFix()) {
        return;
    }

    const target = instance.wrapper || instance.control || instance.input;
    if (!target) {
        return;
    }

    clearScheduledMobileScroll(instance);

    instance._mobileScrollTimeouts = [window.setTimeout(() => {
        const scrollOffset = getStickyHeaderOffset();
        const targetTop = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - scrollOffset);

        window.scrollTo({
            top: targetTop,
            behavior: 'smooth',
        });
    }, MOBILE_SCROLL_DELAY)];
}

function createSettings(selectEl, customSettings = {}) {
    const isRequired = selectEl.required;
    const ariaLabel = resolveAriaLabel(selectEl);
    const dataset = selectEl.dataset || {};
    const datasetSettings = getSettingsFromDataset(selectEl);
    const plugins = {};
    const {
        onInitialize: customOnInitialize,
        onFocus: customOnFocus,
        onDropdownOpen: customOnDropdownOpen,
        plugins: customPlugins = {},
        ...restCustomSettings
    } = customSettings;

    if (!isRequired) {
        plugins.clear_button = {
            title: 'Vymazat výběr',
        };
    }

    return {
        create: false,
        maxOptions:10000,
        placeholder: dataset.placeholder || null,
        plugins: {
            ...plugins,
            ...customPlugins,
        },
        render:{
            no_results:function(data,escape){
                return '<div class="no-results">Žádné výsledky pro "'+escape(data.input)+'"</div>';
            },
        },
        ...datasetSettings,
        onInitialize() {
            if (!this.getValue() || this.getValue() === '') {
                this.clear(true);
            }

            clearScheduledMobileScroll(this);

            const emptyOpt = this.getOption('');
            if (emptyOpt) emptyOpt.style.display = 'none';

            const controlInput = this.control_input;
            if (controlInput && ariaLabel) {
                controlInput.setAttribute('aria-label', ariaLabel);
            }

            if (typeof customOnInitialize === 'function') {
                customOnInitialize.call(this);
            }
        },
        onFocus(...args) {
            scrollTomSelectIntoView(this);

            if (typeof customOnFocus === 'function') {
                return customOnFocus.apply(this, args);
            }
        },
        onDropdownOpen(...args) {
            scrollTomSelectIntoView(this);

            if (typeof customOnDropdownOpen === 'function') {
                return customOnDropdownOpen.apply(this, args);
            }
        },
        ...restCustomSettings,
    };
}

export function initTomSelectForElement(selectEl, customSettings = {}) {
    if (!selectEl || selectEl.tagName !== 'SELECT') {
        return null;
    }

    if (selectEl.multiple || selectEl.disabled || selectEl.dataset.noTomSelect === '1') {
        return null;
    }

    if (selectEl.tomselect) {
        return selectEl.tomselect;
    }

    const instance = new TomSelect(selectEl, createSettings(selectEl, customSettings));
    applyVisualStylesFromSelect(selectEl, instance);
    return instance;
}

export function initTomSelects(container = document) {
    container.querySelectorAll('select.tom-select, select[data-tom-select="1"]').forEach((selectEl) => {
        initTomSelectForElement(selectEl);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initTomSelects();
    });
} else {
    initTomSelects();
}

if (window.jQuery) {
    window.jQuery(document).ajaxComplete(() => {
        initTomSelects();
    });
}

window.initTomSelectForElement = initTomSelectForElement;
window.initTomSelects = initTomSelects;
window.TomSelect = TomSelect;
